import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { FormikProps } from 'formik'
import { LoginMethodFormBlockProps } from 'src/components/LoginMethodFormBlock'
import { useSagaTakeEvery } from 'src/hooks/useSaga'
import { Provider } from 'src/rest/types/User'
import Icon, { Icons } from 'src/components/Icon'
import Router from 'src/routes/Router'
import routes from 'src/routes/definitions'
import { api } from 'src/configuration'
import { setEmailCookie } from 'src/helpers'

import { useServiceRequest } from '../../hooks/useServiceRequest'
import { actions, selectors } from '../../redux'
import { FormFieldOrientation } from '../../types/form'
import useMediaQuery from '../../hooks/useMediaQuery'
import tracking from '../../tracking'

const useLoginMethodFormProps = () => {
  const formikRef = useRef<FormikProps<any>>(null)
  const { t } = useTranslation()
  const matches = useMediaQuery('(max-width: 576px)')
  const [isEcps, setIsEcps] = useState<boolean>(false)
  const [isLocal, setIsLocal] = useState<boolean>(false)
  const [isShown, setIsShown] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [, setShowLoginError] = useState<boolean>(false)
  const [showSubsequent, setShowSubsequent] = useState<boolean>(false)

  const [response, loginMethod] = useServiceRequest(
    selectors.auth.loginMethod,
    actions.auth.loginMethodRequest,
    actions.auth.loginMethodReset,
    () => formikRef.current?.resetForm()
  )

  const [loginResonse, login] = useServiceRequest(
    selectors.auth.emailLogin,
    actions.auth.emailLoginRequest,
    actions.auth.emailLoginReset,
    () => formikRef.current?.resetForm()
  )

  useSagaTakeEvery(
    actions.auth.loginMethodSuccess,
    useCallback(
      ({ payload }) => {
        if (payload?.data.provider === Provider.Local) {
          setIsLocal(true)
        }

        if (
          payload?.data.provider &&
          [Provider.EcpsBas, Provider.Ecps].includes(
            payload.data.provider as Provider
          )
        ) {
          setIsEcps(true)
        }

        setEmailCookie(email)

        formikRef.current?.resetForm()
      },
      [email]
    )
  )

  useSagaTakeEvery(
    actions.auth.emailLoginSuccess,
    useCallback(({ payload }) => {
      tracking.connexion(payload?.user)
    }, [])
  )

  useSagaTakeEvery(
    actions.auth.loginMethodError,
    useCallback(() => {
      setShowSubsequent(true)
      formikRef.current?.resetForm()
    }, [])
  )

  const goBack = () => {
    setIsEcps(false)
    setIsLocal(false)
    setShowSubsequent(false)
    setEmail('')
    formikRef.current?.resetForm()
  }

  const loginMethodFormBlockProps: LoginMethodFormBlockProps = useMemo(
    () => ({
      ...(!isLocal &&
        !isEcps && {
          blockTitle: t('login_method_block_default_title'),
        }),
      ...(isLocal && {
        blockTitle: t('login_method_block_local_title'),
      }),
      ...(isEcps && {
        blockTitle: t('login_method_block_ecps_title'),
        button: {
          href: `${api.API_URL}/api/connect/${api.AUTH_PROVIDER}`,
          text: t('register_ecps_block_left_button'),
        },
      }),
      ...(email &&
        (isEcps || isLocal) && {
          email,
          topBackLink: {
            text: t('login_method_block_topbacklink'),
            onClick: () => {
              setIsEcps(false)
              setIsLocal(false)
              setEmail('')
              formikRef.current?.resetForm()
            },
          },
        }),
      ...(!isEcps && {
        form: {
          innerRef: formikRef,
          validationSchema: Yup.object().shape({
            ...(!isLocal && {
              email: Yup.string()
                .required(t('yup_required'))
                .email(t('yup_email')),
            }),
            ...(isLocal && {
              password: Yup.string().required(t('yup_required')),
            }),
          }),
          initialValues: {
            ...(!isLocal && {
              email: '',
            }),
            ...(isLocal && {
              password: '',
            }),
          },
          fields: {
            ...(!isLocal && {
              email: {
                placeholder: t('login_method_block_form_email_placeholder'),
                label: t('login_method_block_form_email'),
                innerError: true,
                required: true,
                ...(matches && {
                  orientation: FormFieldOrientation.Vertical,
                }),
              },
            }),
            ...(isLocal && {
              password: {
                label: t('login_method_block_form_password'),
                placeholder: t('login_method_block_form_password_placeholder'),
                innerError: true,
                required: true,
                ...(matches && {
                  orientation: FormFieldOrientation.Vertical,
                }),
                ...(!isShown && {
                  rightAdornment: (
                    <Icon
                      onClick={() => setIsShown((isShown) => !isShown)}
                      icon={Icons.eye}
                      width={22}
                      height={24}
                    />
                  ),
                  type: 'password',
                }),
                ...(isShown && {
                  rightAdornment: (
                    <Icon
                      onClick={() => setIsShown((isShown) => !isShown)}
                      icon={Icons.eyeSlash}
                      width={22}
                      height={24}
                    />
                  ),
                  type: 'text',
                }),
              },
            }),
          },
          onSubmit: (values) => {
            setShowLoginError(false)

            if (values.email) {
              setEmail(values.email)

              loginMethod({
                userEmail: values.email,
              })
            }

            if (values.password && email) {
              login({
                identifier: email,
                password: values.password,
              })
            }
          },
          ...(!isLocal && {
            innerActions: {
              submitButton: {
                text: t('login_method_block_form_validate'),
                isLoading: response.pending,
              },
            },
          }),
          ...(isLocal && {
            bottomActions: {
              submitButton: {
                text: t('login_method_block_form_login'),
                isLoading: loginResonse.pending,
              },
              forgottenPassword: {
                text: t('login_method_block_forgotten_password'),
                href: Router.getRouteUrl(routes.forgottenPassword),
              },
            },
          }),
          ...(response.errors && {
            errors: [{ label: t('login_method_error') }],
          }),
          ...((loginResonse.errors ||
            (response.complete && !response.success)) && {
            errors: [{ label: t('login_error') }],
          }),
        },
      }),
      ...((isEcps || isLocal) && {
        backLink: {
          text: t('login_method_block_form_back_link'),
          onClick: () => {
            setIsEcps(false)
            setIsLocal(false)
            setEmail('')
            formikRef.current?.resetForm()
          },
        },
      }),
    }),
    [
      t,
      matches,
      loginMethod,
      isShown,
      isLocal,
      isEcps,
      login,
      email,
      loginResonse,
      response,
    ]
  )

  return { loginMethodFormBlockProps, showSubsequent, goBack }
}

export default useLoginMethodFormProps
